import React, { Children, cloneElement, type ReactNode, useEffect, useId } from "react";
import { Heading, type HeadingTagType } from "@purpur/heading";
import * as RadixToggleGroup from "@radix-ui/react-toggle-group";
import c from "classnames/bind";

import styles from "./chip-group.module.scss";
import { ChipGroupItem, isChipGroupItem } from "./chip-group-item";

type StylingProps = Pick<React.HTMLAttributes<HTMLElement>, "style" | "className">;

type ChipGroupProps = StylingProps & {
  ["data-testid"]?: string;
  /**
   * Will only render given ChipGroupItems.
   * */
  children: ReactNode;
  /**
   * Makes the chips span the full container width if set to `true`.
   * */
  fullWidth?: boolean;
  /**
   * Renders above the chip group as a heading.
   * */
  title?: ReactNode;
  /**
   * Allows to customise title.
   */
  titleProps?: TitlePropsType;
  /**
   * Optional name for form submission
   */
  name?: string;
};

export type TitlePropsType = StylingProps & {
  /**
   * Use custom id for connecting title and chip group.
   */
  id?: string;
  /**
   * Changes heading level.
   * @default h2
   */
  tag?: HeadingTagType;
};

export type ChipGroupChoiceProps = {
  /**
   * Event handler called when the pressed state of an item changes.
   * */
  onValueChange: (value: string) => void;
  /**
   * Determines whether a single (`"choice"`) or multiple (`"filter"`) items can be pressed at a time.
   * */
  type: "choice";
  /**
   * The value of the pressed item(s).
   * */
  value: string;
};

export type ChipGroupFilterProps = {
  /**
   * Event handler called when the pressed state of an item changes.
   * */
  onValueChange: (value: string[]) => void;
  /**
   * Determines whether a single (`"choice"`) or multiple (`"filter"`) items can be pressed at a time.
   * */
  type: "filter";
  /**
   * The value of the pressed item(s).
   * */
  value: string[];
};

const isChoiceProps = (
  props: ChipGroupChoiceProps | ChipGroupFilterProps
): props is ChipGroupChoiceProps => props.type === "choice";

type ChipGroupCmp<P> = React.FunctionComponent<P> & {
  Item: typeof ChipGroupItem;
};

const MIN_CHOICE_OPTIONS = 2;

const cx = c.bind(styles);
const rootClassName = "purpur-chip-group";

export const ChipGroup: ChipGroupCmp<
  ChipGroupProps & (ChipGroupChoiceProps | ChipGroupFilterProps)
> = ({ children, className, fullWidth = false, title, titleProps, name, ...props }) => {
  const classes = cx([
    rootClassName,
    {
      [`${rootClassName}--full-width`]: fullWidth,
    },
    className,
  ]);

  const id = useId();

  const chipGroupItemChildren = Children.toArray(children).filter(isChipGroupItem);

  const clonedChildren = Children.map(chipGroupItemChildren, (child) =>
    cloneElement(child, {
      showCheckmark: props.type === "filter" && props.value.includes(child.props.value),
      unavailable: props.type === "choice" && child.props.unavailable,
    })
  );

  const handleValueChange = (value: string & string[]) => {
    if (props.type === "filter" || value !== "") {
      props.onValueChange(value);
    }
  };

  useEffect(() => {
    if (props.type === "choice" && clonedChildren.length < MIN_CHOICE_OPTIONS) {
      console.warn(
        `Minimum number of chips should be ${MIN_CHOICE_OPTIONS} when using the "choice" type`
      );
    }
  }, [clonedChildren, props.type]);

  const chipGroupChildren = () => {
    if (isChoiceProps(props)) {
      return (
        <RadixToggleGroup.Root
          className={classes}
          {...props}
          aria-labelledby={title ? `${titleProps?.id ?? id}-label` : undefined}
          onValueChange={handleValueChange}
          type="single"
        >
          {clonedChildren}
        </RadixToggleGroup.Root>
      );
    }
    return (
      <RadixToggleGroup.Root
        className={classes}
        {...props}
        aria-labelledby={title ? `${titleProps?.id ?? id}-label` : undefined}
        onValueChange={handleValueChange}
        type="multiple"
      >
        {clonedChildren}
      </RadixToggleGroup.Root>
    );
  };

  return (
    <div className={styles[`${rootClassName}--container`]}>
      {title && (
        <Heading
          tag="h2"
          variant="subsection-100"
          {...titleProps}
          id={`${titleProps?.id ?? id}-label`}
        >
          {title}
        </Heading>
      )}
      {chipGroupChildren()}
      {name && props.type === "choice" && <input type="hidden" name={name} value={props.value} />}
    </div>
  );
};

ChipGroup.Item = ChipGroupItem;

ChipGroup.displayName = "ChipGroup";
